/**
 * GA4 property "Apex25", shared by tryapex25.com (marketing) and recruit.theresume.io (app).
 * Replaces the hardcoded G-4EBS1GK852 tag that index.html and providers/analytics.tsx both
 * loaded (every page view was counted twice). Loaded once, only when VITE_GA4_MEASUREMENT_ID is
 * set for the build.
 *
 * Cross-domain: the linker decorates links between these domains with `_gl`, so a visitor who
 * clicks from tryapex25.com into the app keeps one GA client id and session.
 */
const LINKED_DOMAINS = ["tryapex25.com", "apex25.theresume.io", "recruit.theresume.io"];

// window.dataLayer / window.gtag are declared globally in hooks/useAnalytics.ts.

let measurementId: string | undefined;

export const initGa4 = () => {
  if (measurementId || typeof window === "undefined") return;
  const id = import.meta.env.VITE_GA4_MEASUREMENT_ID?.trim();
  if (!id || !/^G-[A-Z0-9]+$/.test(id)) return;

  try {
    window.dataLayer = window.dataLayer ?? [];
    // gtag.js reads the `arguments` object, so this must stay a function expression.
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", id, {
      // Page views are sent by the router tracker with a cleaned URL.
      send_page_view: false,
      linker: { domains: LINKED_DOMAINS, accept_incoming: true },
      allow_google_signals: false,
    });

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.append(script);

    measurementId = id;
  } catch {
    // Analytics must never break the page.
  }
};

export const ga4Event = (name: string, params: Record<string, unknown> = {}) => {
  if (!measurementId) return;
  try {
    window.gtag("event", name, params);
  } catch {
    // ignore
  }
};

/** Internal Recruiter.id only (never email). Pass null on sign-out. */
export const ga4SetUser = (userId: string | null) => {
  if (!measurementId) return;
  try {
    window.gtag("set", { user_id: userId });
  } catch {
    // ignore
  }
};
